import * as THREE from 'three';

/**
 * Creates a beautiful infinite starfield background using point clouds.
 * Stars are drawn with a soft radial sprite (not square points) and use a
 * physically-inspired color mix: mostly warm whites, some blue giants,
 * a few orange/red dwarfs.
 */
export class Starfield {
  constructor(scene) {
    this.scene = scene;
    this.particles = [];
    this._starTexture = this._createStarTexture();
    this._create();
  }

  /** Soft radial glow sprite generated at runtime — no asset needed. */
  _createStarTexture() {
    const size = 64;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.25, 'rgba(255,255,255,0.8)');
    g.addColorStop(0.6, 'rgba(255,255,255,0.15)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  }

  /** Pick a star color: warm white majority, blue giants, rare red dwarfs. */
  _starColor(target) {
    const r = Math.random();
    if (r < 0.62)      target.setHSL(0.12, 0.10 + Math.random() * 0.1, 0.85 + Math.random() * 0.15); // warm white
    else if (r < 0.85) target.setHSL(0.60, 0.45 + Math.random() * 0.25, 0.75 + Math.random() * 0.2);  // blue-white
    else if (r < 0.96) target.setHSL(0.55, 0.15, 0.65 + Math.random() * 0.2);                          // pale
    else               target.setHSL(0.05, 0.65, 0.60 + Math.random() * 0.15);                         // orange dwarf
    return target;
  }

  _create() {
    // Layer 1: Dense nearby stars
    this._addLayer(4000, 500, 2.2, 0.9);
    // Layer 2: Mid-distance
    this._addLayer(3000, 1500, 1.4, 0.55);
    // Layer 3: Far, faint stars
    this._addLayer(5000, 3000, 0.8, 0.35);
    // Layer 4: Colorful nebula-like clusters
    this._addNebulaCloud(200, 800, 14);
  }

  _addLayer(count, spread, size, opacity) {
    const geo = new THREE.BufferGeometry();
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const c = new THREE.Color();

    for (let i = 0; i < count; i++) {
      positions[i * 3]     = (Math.random() - 0.5) * spread;
      positions[i * 3 + 1] = (Math.random() - 0.5) * spread;
      positions[i * 3 + 2] = (Math.random() - 0.5) * spread;

      this._starColor(c);
      colors[i * 3]     = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
    }

    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const mat = new THREE.PointsMaterial({
      size,
      map: this._starTexture,
      vertexColors: true,
      transparent: true,
      opacity,
      sizeAttenuation: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    const points = new THREE.Points(geo, mat);
    this.scene.add(points);
    this.particles.push(points);
  }

  _addNebulaCloud(count, spread, size) {
    const geo = new THREE.BufferGeometry();
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);

    // Random nebula color
    const hue = Math.random();

    for (let i = 0; i < count; i++) {
      // Cluster them together
      const cx = (Math.random() - 0.5) * spread;
      const cy = (Math.random() - 0.5) * spread * 0.3;
      const cz = (Math.random() - 0.5) * spread;
      positions[i * 3]     = cx;
      positions[i * 3 + 1] = cy;
      positions[i * 3 + 2] = cz;

      const c = new THREE.Color();
      c.setHSL(hue + Math.random() * 0.1, 0.8, 0.3 + Math.random() * 0.2);
      colors[i * 3]     = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
    }

    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const mat = new THREE.PointsMaterial({
      size,
      map: this._starTexture,
      vertexColors: true,
      transparent: true,
      opacity: 0.12,
      sizeAttenuation: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    const points = new THREE.Points(geo, mat);
    this.scene.add(points);
    this.particles.push(points);
  }

  /** Keep starfield centered on camera for infinite effect */
  update(cameraPosition) {
    for (const p of this.particles) {
      p.position.copy(cameraPosition);
    }
  }
}
